import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Order, OrderDetail, CreateOrderData, OrderStatusChoices } from '../../types';

interface OrdersState {
  orders: Order[];
  currentOrder: OrderDetail | null;
  statusChoices: OrderStatusChoices | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrdersState = {
  orders: [],
  currentOrder: null,
  statusChoices: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async () => {
    const response = await api.get('/orders/');
    return response.data;
  }
);

export const fetchOrderDetail = createAsyncThunk(
  'orders/fetchOrderDetail',
  async (orderId: string) => {
    const response = await api.get(`/orders/${orderId}/`);
    return response.data;
  }
);

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData: CreateOrderData) => {
    const response = await api.post('/orders/create/', orderData);
    return response.data;
  }
);

export const fetchOrderStatusChoices = createAsyncThunk(
  'orders/fetchStatusChoices',
  async () => {
    const response = await api.get('/orders/status-choices/');
    return response.data;
  }
);

// Az admin rendelés-lista és -állapotváltás a Műhely API-rétegén megy (src/muhely/api.ts)
// — így nem írja felül a vevő saját rendelés-listáját ebben a slice-ban.
// Admin: egy rendelés részletei (a vevői rendelés-oldal admin nézete).
export const fetchAdminOrderDetail = createAsyncThunk(
  'orders/fetchAdminOrderDetail',
  async (orderId: string) => {
    const response = await api.get(`/admin/orders/${orderId}/`);
    return response.data;
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.results || action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
      })

      // Fetch order detail
      .addCase(fetchOrderDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch order details';
      })

      // Create order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload.order;
        // Add to orders list if it's the current user's order
        if (action.payload.order) {
          state.orders.unshift(action.payload.order);
        }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create order';
      })

      // Fetch status choices
      .addCase(fetchOrderStatusChoices.fulfilled, (state, action) => {
        state.statusChoices = action.payload;
      })

      // Admin fetch order detail
      .addCase(fetchAdminOrderDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchAdminOrderDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch admin order details';
      });
  },
});

export const { clearCurrentOrder, clearError } = ordersSlice.actions;
export default ordersSlice.reducer;